// In-game HUDs: level HUD, boss HUD, Brain Blitz HUD, title card + GO!
import * as THREE from 'three';
import { ICON, heartSVG } from './icons.js';
import { fmtTime, fmtNum } from './util.js';

const _v = new THREE.Vector3();

export class HUD {
  constructor(ui) {
    this.ui = ui;
    this.el = document.createElement('div');
    this.el.id = 'hud';
    this.el.innerHTML = `
      <div class="hud-tl">
        <div class="hud-hearts"></div>
        <div class="hud-powers"></div>
      </div>
      <div class="hud-tc">
        <div class="hud-level"><span class="hud-wl"></span><span class="hud-name"></span></div>
        <div class="hud-progress"><div class="hp-track"><div class="hp-fill"></div><div class="hp-sai"></div></div><div class="hp-end">🌀</div></div>
        <div class="hud-time"></div>
      </div>
      <div class="hud-tr">
        <div class="pill coin-pill"><span class="pill-ico">${ICON.coin}</span><span class="hud-coins">0</span></div>
        <div class="pill gem-pill"><span class="pill-ico">${ICON.gem}</span><span class="hud-gems">0</span></div>
        <div class="hud-stars" title="Stars"><span class="hs hs1">${ICON.star}</span><span class="hs hs2">${ICON.star}</span><span class="hs hs3">${ICON.star}</span></div>
        <button class="hud-btn mode-home" data-act="modeHome" aria-label="Home screen, swap game modes">⌂<small>Menu</small></button><button class="hud-btn hud-pause" data-act="pause" aria-label="Pause">${ICON.pause}</button>
      </div>
      <div class="hud-streak"></div>
      <div class="hud-goal"></div>`;
    ui.root.appendChild(this.el);
    this.$ = (s) => this.el.querySelector(s);
    this.last = {};
    this.coinTarget = this.$('.coin-pill');
  }
  show(level) {
    this.el.classList.add('show');
    this.level = level;
    this.$('.hud-wl').textContent = level.expedition ? "QUEST" : level.isBoss ? `WORLD ${level.wi} · BOSS` : `${level.wi}-${level.li}`;
    this.$('.hud-name').textContent = level.name;
    this.$('.hud-progress').style.display = level.isBoss ? 'none' : '';
    this.$('.hs3').title = level.isBoss ? 'Take 1 hit or less' : 'Find the secret star';
    this.$('.hs3').classList.remove('on');
    this.$('.hs2').classList.remove('on');
    this.last = {};
    const face = this.ui.faceCanvas();
    face.classList.add('hp-face');
    const sai = this.$('.hp-sai');
    sai.innerHTML = '';
    sai.appendChild(face);
  }
  hide() {
    this.el.classList.remove('show');
  }
  update(L) {
    const pl = L.player;
    const set = (k, v, fn) => {
      if (this.last[k] !== v) {
        this.last[k] = v;
        fn(v);
      }
    };
    set('hearts', pl.hearts + '/' + pl.maxHearts, () => {
      let h = '';
      for (let i = 0; i < pl.maxHearts; i++) h += `<span class="heart ${i < pl.hearts ? 'full' : 'empty'}">${heartSVG(i < pl.hearts)}</span>`;
      const el = this.$('.hud-hearts');
      el.innerHTML = h;
      el.classList.remove('shake');
      void el.offsetWidth;
      if (this.heartsPrev !== undefined && pl.hearts < this.heartsPrev) el.classList.add('shake');
      this.heartsPrev = pl.hearts;
    });
    set('coins', L.stats.coins, (v) => (this.$('.hud-coins').textContent = fmtNum(v)));
    set('gems', L.stats.gems, (v) => (this.$('.hud-gems').textContent = fmtNum(v)));
    set('time', Math.floor(L.stats.time || 0), (v) => (this.$('.hud-time').textContent = '⏱ ' + fmtTime(v)));
    const prog = L.isBoss ? 0 : L.progress();
    set('prog', Math.round(prog * 200), () => {
      this.$('.hp-fill').style.width = prog * 100 + '%';
      this.$('.hp-sai').style.left = prog * 100 + '%';
    });
    const acc = L.stats.asked ? L.stats.firstTry / L.stats.asked : 1;
    set('brain', acc >= 0.8 && L.stats.asked > 0, (v) => this.$('.hs2').classList.toggle('on', v));
    set('secret', L.isBoss ? pl.damageTaken <= 1 : L.stats.secret, (v) => this.$('.hs3').classList.toggle('on', !!v));
    const streak = L.game.quiz.streak;
    set('streak', streak, (v) => {
      const el = this.$('.hud-streak');
      el.innerHTML = v >= 2 ? `<span class="flame">🔥</span><b>${v}</b><small>streak</small>` : '';
      el.classList.toggle('show', v >= 2);
      el.classList.toggle('hot', v >= 5);
    });
    // power-ups
    const pw = pl.power;
    const ps = Object.entries(pw).filter(([, t]) => t > 0).map(([k, t]) => `${k}:${Math.ceil(t)}`).join(',');
    set('powers', ps, () => {
      const icons = { magnet: '🧲', speed: '⚡', jump: '🦘', shield: '🛡️' };
      this.$('.hud-powers').innerHTML = Object.entries(pw)
        .filter(([, t]) => t > 0)
        .map(([k, t]) => `<div class="power"><span>${icons[k]}</span><b>${Math.ceil(t)}</b></div>`)
        .join('');
    });
    const teachingMoves=L.id==='1-1'&&!L.save.levels['1-1']?.done;
    const goal=L.isBoss?'👑 Dodge, then hit the pillar with the right answer!':L.lastOffer && L.facingHittable()?'⚔️ Use Hit to clear the way, then solve the puzzle.':L.lastOffer?'🧠 Press '+(L.game.input.touchMode?'the glowing action button':'E')+' to try this puzzle':teachingMoves?(prog<.08?'👣 Follow the coins. Jump with '+(L.game.input.touchMode?'Jump':'Space')+'.':'🌀 Keep following the path to the portal. Falling brings you back safely.') : '';
    set('goal',goal,v=>{const el=this.$('.hud-goal');el.textContent=v;el.hidden=!v;});
    set('dj', !!L.game.save.data.unlocks.doubleJump, () => {});
  }
  bump(which) {
    const el = this.$(which === 'gems' ? '.gem-pill' : '.coin-pill');
    el.classList.remove('bump');
    void el.offsetWidth;
    el.classList.add('bump');
  }
  coinFly(pos) {
    const cam = this.ui.game.camera;
    _v.copy(pos).project(cam);
    if (_v.z > 1) return this.bump('coins');
    const x = (_v.x * 0.5 + 0.5) * innerWidth, y = (-_v.y * 0.5 + 0.5) * innerHeight;
    const r = this.coinTarget.getBoundingClientRect();
    const el = document.createElement('div');
    el.className = 'coin-fly';
    el.innerHTML = ICON.coin;
    el.style.transform = `translate(${x}px, ${y}px)`;
    document.body.appendChild(el);
    requestAnimationFrame(() => {
      el.style.transform = `translate(${r.left + 18}px, ${r.top + r.height / 2}px) scale(0.7)`;
      el.style.opacity = '0.9';
    });
    setTimeout(() => {
      el.remove();
      this.bump('coins');
    }, 520);
  }
  starFound() {
    const el = this.$('.hs3');
    el.classList.add('on', 'pop');
    setTimeout(() => el.classList.remove('pop'), 900);
  }
}

export class BossHUD {
  constructor(ui) {
    this.ui = ui;
    this.el = document.createElement('div');
    this.el.id = 'boss-hud';
    this.el.innerHTML = `
      <div class="bh-head"><span class="bh-skull">👑</span><span class="bh-name"></span></div>
      <div class="bh-bar"><div class="bh-fill"></div><div class="bh-segs"></div></div>
      <div class="bh-q"><div class="bh-qtext"></div><div class="bh-qsub"></div><div class="bh-timer"><div></div></div></div>
      <div class="bh-msg"></div>`;
    ui.root.appendChild(this.el);
    this.$ = (s) => this.el.querySelector(s);
  }
  show(name, hp, max) {
    this.max = max;
    this.el.classList.add('show');
    this.$('.bh-name').textContent = name;
    this.$('.bh-segs').innerHTML = Array.from({ length: max - 1 }, (_, i) => `<i style="left:${((i + 1) / max) * 100}%"></i>`).join('');
    this.setHp(hp);
    this.question(null);
  }
  setHp(hp) {
    this.$('.bh-fill').style.width = (Math.max(0, hp) / this.max) * 100 + '%';
    const b = this.$('.bh-bar');
    b.classList.remove('hit');
    void b.offsetWidth;
    b.classList.add('hit');
  }
  question(html, sub = '') {
    const q = this.$('.bh-q');
    if (!html) {
      q.classList.remove('show');
      return;
    }
    this.$('.bh-qtext').innerHTML = html;
    this.$('.bh-qsub').innerHTML = sub;
    q.classList.add('show');
  }
  timer(f) {
    this.$('.bh-timer div').style.width = Math.max(0, f) * 100 + '%';
  }
  message(html, dur = 3.5) {
    const m = this.$('.bh-msg');
    m.innerHTML = html;
    m.classList.add('show');
    clearTimeout(this.mt);
    this.mt = setTimeout(() => m.classList.remove('show'), dur * 1000);
  }
  hide() {
    this.el.classList.remove('show');
    this.question(null);
    this.$('.bh-msg').classList.remove('show');
  }
}

export class BlitzHUD {
  constructor(ui) {
    this.ui = ui;
    this.el = document.createElement('div');
    this.el.id = 'blitz-hud';
    this.el.innerHTML = `
      <button class="btn btn-small btn-dark blitz-home" data-act="modeHome">⌂ Home screen</button><div class="bz-top">
        <div class="bz-box bz-timebox"><small>TIME</small><div class="bz-time">60</div></div>
        <div class="bz-q"></div>
        <div class="bz-box"><small>SCORE</small><div class="bz-score">0</div></div>
      </div>
      <div class="bz-combo"></div>
      <div class="bz-hint"></div>
      <div class="bz-count"></div>
      <div class="bz-help">Run to the right block and hit it ⚔️ — or tap it / press 1-4!</div>`;
    ui.root.appendChild(this.el);
    this.$ = (s) => this.el.querySelector(s);
  }
  show() {
    this.el.classList.add('show');
    this.$('.bz-q').innerHTML = '';
    this.$('.bz-hint').classList.remove('show');
  }
  hide() {
    this.el.classList.remove('show');
  }
  question(html) {
    const q = this.$('.bz-q');
    q.innerHTML = html || '';
    q.classList.remove('pop');
    void q.offsetWidth;
    if (html) q.classList.add('pop');
    this.$('.bz-hint').classList.remove('show');
  }
  hint(html) {
    const h = this.$('.bz-hint');
    h.innerHTML = html;
    h.classList.add('show');
  }
  count(text) {
    const c = this.$('.bz-count');
    c.textContent = text;
    c.classList.remove('go');
    void c.offsetWidth;
    c.classList.add('go');
  }
  update(B) {
    const t = Math.max(0, B.timeLeft);
    const te = this.$('.bz-time');
    const s = Math.ceil(t).toString();
    if (te.textContent !== s) te.textContent = s;
    this.$('.bz-timebox').classList.toggle('low', t <= 10);
    const sc = fmtNum(B.score);
    const se = this.$('.bz-score');
    if (se.textContent !== sc) se.textContent = sc;
    const cb = this.$('.bz-combo');
    const ch = B.combo >= 2 ? `🔥 x${B.combo} COMBO` : '';
    if (cb.textContent !== ch) cb.textContent = ch;
  }
}

export class TitleCard {
  constructor(ui) {
    this.el = document.createElement('div');
    this.el.id = 'title-card';
    ui.root.appendChild(this.el);
    this.go = document.createElement('div');
    this.go.id = 'go-splash';
    ui.root.appendChild(this.go);
  }
  show(L) {
    const goals = L.isBoss
      ? `<span>⭐ Defeat the boss</span><span>🧠 80% right first time</span><span>❤️ Take 1 hit or less</span>`
      : `<span>⭐ Reach the portal</span><span>🧠 80% right first time</span><span>🔍 Find the secret star</span>`;
    this.el.innerHTML = `<div class="tc-world">${L.expedition ? "DISCOVERY ADVENTURE" : L.isBoss ? `WORLD ${L.wi} · BOSS BATTLE` : `WORLD ${L.wi} · LEVEL ${L.li}`}</div><div class="tc-name">${L.name}</div><div class="tc-goals">${goals}</div><div class="tc-skip">Tap or press any key to skip ⏩</div>`;
    this.el.classList.add('show');
  }
  hide() {
    this.el.classList.remove('show');
    this.go.classList.remove('show');
  }
  splash(text = 'GO!') {
    this.go.textContent = text;
    this.go.classList.remove('show');
    void this.go.offsetWidth;
    this.go.classList.add('show');
  }
}
